import React from "react";

export enum PopupCardName {
  Login = "login",
  Register = "register",
  LoginLoading = "loginLoading",
  TableLoading = "tableLoading",
  Logout = "logout",
  CoinConfirm = "coinConfirm",
  Failed = "failed",
  Notice = "notice",
}

interface PopupCardProps {
  name: PopupCardName;
  status?: "success" | "failed";
  text?: React.ReactNode;
  onClose?: () => void;
  onLogout?: () => void;
  onConfirm?: () => void;
}

interface StatusPopupContentProps {
  icon: string;
  alt: string;
  children: React.ReactNode;
}

const CLOSEABLE_POPUPS = new Set<PopupCardName>([
  PopupCardName.Login,
  PopupCardName.Register,
  PopupCardName.LoginLoading,
  PopupCardName.TableLoading,
  PopupCardName.Failed,
  PopupCardName.Notice,
]);

const STATUS_ICON_CLASS_NAME = "w-24 md:w-36 shrink-0 object-contain";
const OVERLAY_CLASS_NAME =
  "fixed top-0 left-0 z-50 flex h-full w-full items-center justify-center bg-black/70";
const CARD_CLASS_NAME =
  "relative isolate flex min-h-[250px] w-[min(90vw,420px)] flex-col items-center justify-center px-6 py-8 md:min-h-[280px] md:w-[480px] md:px-10";

const ActionButtons: React.FC<{
  onAccept?: () => void;
  onCancel?: () => void;
}> = ({ onAccept, onCancel }) => (
  <div className="flex items-center justify-center gap-2">
    <button onClick={onAccept}>
      <img src="/assets/common/btn-accept.png" alt="btn accept" />
    </button>
    <button onClick={onCancel}>
      <img src="/assets/common/btn-cancel.png" alt="btn cancel" />
    </button>
  </div>
);

const StatusPopupContent: React.FC<StatusPopupContentProps> = ({
  icon,
  alt,
  children,
}) => (
  <div className="flex flex-col items-center justify-center gap-3">
    <img className={STATUS_ICON_CLASS_NAME} src={icon} alt={alt} />
    {children}
  </div>
);

const PopupCard: React.FC<PopupCardProps> = ({
  name,
  status = "success",
  text,
  onClose,
  onLogout,
  onConfirm,
}) => {
  const isCloseable = CLOSEABLE_POPUPS.has(name);
  const isSuccess = status === "success";
  const statusIcon = isSuccess
    ? "/assets/common/icon-success.png"
    : "/assets/common/icon-failed.png";
  const statusAlt = isSuccess ? "success icon" : "failed icon";

  const renderContent = () => {
    switch (name) {
      case PopupCardName.Login:
        return (
          <StatusPopupContent icon={statusIcon} alt={statusAlt}>
            <div className="flex items-center justify-center">
              <div className="px-4 text-center text-xl font-bold uppercase text-white md:text-2xl">
                {isSuccess ? "ĐĂNG NHẬP THÀNH CÔNG!" : "ĐĂNG NHẬP THẤT BẠI!"}
              </div>
            </div>
          </StatusPopupContent>
        );

      case PopupCardName.Register:
        return (
          <StatusPopupContent icon={statusIcon} alt={statusAlt}>
            <div className="flex items-center justify-center">
              <div className="text-center text-2xl font-medium text-white">
                {isSuccess ? (
                  <div className="flex flex-col items-center justify-center gap-3">
                    CHÚC MỪNG ĐÃ ĐĂNG KÝ <br /> THÀNH CÔNG!
                  </div>
                ) : (
                  `${text}`
                )}
              </div>
            </div>
          </StatusPopupContent>
        );

      case PopupCardName.Logout:
        return (
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="text-center text-5xl font-bold text-[#FFF600]">
              ĐĂNG XUẤT
            </div>
            <div className="text-white">
              <div className="flex flex-col items-center justify-center gap-3 text-lg font-semibold md:text-2xl">
                Bạn có chắc muốn <br /> đăng xuất không?
              </div>
            </div>
            <ActionButtons onAccept={onLogout} onCancel={onClose} />
          </div>
        );

      case PopupCardName.CoinConfirm:
        return (
          <div className="flex flex-col items-center justify-center gap-3">
            <img
              className={STATUS_ICON_CLASS_NAME}
              src="/assets/common/icon-confirm.png"
              alt="confirm"
            />
            <div className="px-4 text-center text-lg font-medium uppercase text-[#FFF600] md:text-3xl">
              {text || "THỦ THUẬT LV1: MIỄN PHÍ"}
            </div>
            <ActionButtons onAccept={onConfirm} onCancel={onClose} />
          </div>
        );

      case PopupCardName.LoginLoading:
        return (
          <StatusPopupContent
            icon="/assets/common/loading.gif"
            alt="loading"
          >
            <div className="px-4 text-center text-3xl font-medium text-white">
              {text || "Đang kết nối..."}
            </div>
          </StatusPopupContent>
        );

      case PopupCardName.TableLoading:
        return (
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="px-4 text-center text-3xl font-medium text-white">
              {text || "Đang tải dữ liệu trò chơi"}
            </div>
            <img src="/assets/common/loading.gif" alt="loading" />
            <div className="text-center text-xl font-medium text-[#FFF600]">
              Vui lòng chờ trong giây lát...
            </div>
          </div>
        );

      case PopupCardName.Failed:
        return (
          <StatusPopupContent
            icon="/assets/common/icon-failed.png"
            alt="failed icon"
          >
            <div className="flex items-center justify-center">
              <div className="px-4 text-center text-2xl font-medium text-white">
                {text}
              </div>
            </div>
          </StatusPopupContent>
        );

      case PopupCardName.Notice:
        return (
          <StatusPopupContent icon={statusIcon} alt={statusAlt}>
            <div className="flex items-center justify-center">
              <div className="px-4 text-center text-2xl font-medium text-white">
                {text}
              </div>
            </div>
          </StatusPopupContent>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className={`${OVERLAY_CLASS_NAME} ${isCloseable ? "cursor-pointer" : ""}`}
      onClick={isCloseable ? onClose : undefined}
    >
      <div className={CARD_CLASS_NAME}>
        <img
          className="absolute inset-0 -z-10 h-full w-full object-contain"
          src="/assets/common/popup-bg.png"
          alt="background"
        />
        {renderContent()}
      </div>
    </div>
  );
};

export default PopupCard;
